import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
	PropertyResponse,
	TransferOwnershipBody,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { useProperty } from "../hooks/use-property"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useAuthenticatedUser } from "../user-context"

export function PropertyTransferPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const memberships = usePropertyMemberships(id)
	const { profile } = useAuthenticatedUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tTransfer = t.properties.transfer
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [shareOpen, setShareOpen] = useState(false)
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [confirmName, setConfirmName] = useState("")
	const [formError, setFormError] = useState<string | null>(null)

	const isOwner = memberships.data?.owner.id === profile.id
	const isNotFound = !id || (error instanceof ApiError && error.status === 404)

	const mutation = useMutation({
		mutationFn: (body: TransferOwnershipBody) =>
			apiFetch<PropertyResponse>(
				`/api/common/properties/${id}/transfer-ownership`,
				{ method: "POST", body: JSON.stringify(body) },
			),
		onSuccess: () => {
			// Every property-scoped cache is now stale — the user's
			// relationship to this property just changed and the mainRole
			// filter may hide the property entirely. Drop everything keyed
			// under "properties" and route home.
			queryClient.removeQueries({
				predicate: (q) => q.queryKey[0] === "properties",
			})
			navigate(webPaths.dashboard())
		},
		onError: (err) => {
			setFormError(
				err instanceof ApiError
					? err.message
					: t.properties.form.errors.unknown,
			)
		},
	})

	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading || !property) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (memberships.data && !isOwner) {
		return <Navigate to={webPaths.property(property.id)} replace />
	}

	const otherMembers =
		memberships.data?.memberships.filter((m) => m.user.id !== profile.id) ?? []
	const selectedMember =
		otherMembers.find((m) => m.user.id === selectedId) ?? null
	const confirmMatches = confirmName.trim() === property.name
	const canSubmit = Boolean(
		selectedMember && confirmMatches && !mutation.isPending,
	)

	const selectedDisplayName = selectedMember
		? [selectedMember.user.firstName, selectedMember.user.lastName]
				.filter(Boolean)
				.join(" ")
				.trim() || selectedMember.user.email
		: ""

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer variant="narrow">
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<h1 className="text-2xl font-bold text-foreground">
					{tTransfer.title.replace("{propertyName}", property.name)}
				</h1>
				<p className="mt-3 text-sm text-muted-foreground">
					{tTransfer.explainer}
				</p>

				<form
					className="mt-6 space-y-4"
					onSubmit={(e) => {
						e.preventDefault()
						if (!canSubmit || !selectedMember) return
						setFormError(null)
						mutation.mutate({
							newOwnerId: selectedMember.user.id,
							confirmationName: confirmName,
						})
					}}
				>
					<fieldset className="space-y-2">
						<legend className="block text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-1">
							{tTransfer.memberLabel}
						</legend>
						{otherMembers.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								{tTransfer.emptyState}
							</p>
						) : (
							otherMembers.map((m) => {
								const name =
									[m.user.firstName, m.user.lastName]
										.filter(Boolean)
										.join(" ")
										.trim() || m.user.email
								return (
									<label
										key={m.user.id}
										className={
											"flex items-center justify-between gap-4 px-3 py-2 rounded-xl border cursor-pointer " +
											(selectedId === m.user.id
												? "border-ring bg-accent"
												: "border-border bg-muted/40")
										}
									>
										<div className="min-w-0">
											<p className="text-sm text-foreground truncate">{name}</p>
											<p className="text-xs text-muted-foreground/80 truncate">
												{m.user.email}
											</p>
										</div>
										<div className="flex items-center gap-3">
											<span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
												{t.properties.share.roles[m.role]}
											</span>
											<input
												type="radio"
												name="newOwner"
												value={m.user.id}
												checked={selectedId === m.user.id}
												onChange={() => setSelectedId(m.user.id)}
											/>
										</div>
									</label>
								)
							})
						)}
					</fieldset>

					<label className="block">
						<span className="block text-xs uppercase tracking-[0.12em] text-muted-foreground/80 mb-1">
							{tTransfer.confirmationLabel}
						</span>
						<input
							type="text"
							value={confirmName}
							onChange={(e) => setConfirmName(e.target.value)}
							placeholder={tTransfer.confirmationPlaceholder.replace(
								"{propertyName}",
								property.name,
							)}
							autoComplete="off"
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-red-500 outline-none"
						/>
					</label>

					{formError ? (
						<p role="alert" className="text-sm text-destructive">
							{formError}
						</p>
					) : null}

					<div className="flex items-center gap-2">
						<Button
							type="submit"
							variant="brand"
							size="default"
							disabled={!canSubmit}
							className="bg-red-500 hover:bg-red-600"
						>
							{mutation.isPending
								? tTransfer.submitting
								: tTransfer.submit.replace("{name}", selectedDisplayName)}
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="default"
							onClick={() => navigate(webPaths.property(property.id))}
						>
							{tTransfer.cancel}
						</Button>
					</div>
				</form>
			</PageContainer>

			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
		</div>
	)
}
